// What's new: what each update brings, in the words a shop owner reads (docs/plans/whats-new.md).
// The one source for the app's What's new page, its dashboard card, the Check for updates list
// (as /whats-new.json, emitted by apps/web/vite.config.ts) and CHANGELOG.md
// (`pnpm gen:changelog`). No imports: Node scripts load this file directly.
//
// A branch with something people will notice adds its entry to the newest release whose `date`
// is null (start one if there is none). `/release` sets the date. Newest release first.

/** Who an entry is for: every business, a group of types, or single business types. */
export type WhatsNewFor =
  | 'all'
  | 'shops'
  | 'restaurant'
  | 'workshop'
  | 'kirana'
  | 'pharmacy'
  | 'hardware'
  | 'apparel'
  | 'electronics'
  | 'cosmetics'
  | 'bakery'
  | 'wholesale'
  | 'agrovet'
  | 'general'

export type WhatsNewRole = 'viewer' | 'waiter' | 'staff' | 'manager' | 'admin' | 'owner'

/** The area colours of the sidebar (`Area` in @ims/ui). */
export type WhatsNewArea =
  'sell' | 'people' | 'stock' | 'buy' | 'insight' | 'kitchen' | 'neutral' | 'take'

export type WhatsNewIcon =
  'screen' | 'ticket' | 'chart' | 'mail' | 'home' | 'truck' | 'box' | 'calendar' | 'bell' | 'check'

export interface WhatsNewEntry {
  id: string
  /** What the person gets, never the feature's inside name. */
  title: string
  /** One sentence of why it helps. */
  line: string
  area: WhatsNewArea
  icon: WhatsNewIcon
  for: WhatsNewFor[]
  /** The lowest role that sees it. */
  minRole: WhatsNewRole
  /** Only in this situation. */
  when?: 'several-businesses'
  /** "Show me": a route the app has, for the roles that may open it. */
  show?: { label: string; to: string }
}

export interface WhatsNewFix {
  id: string
  text: string
  for: WhatsNewFor[]
  minRole: WhatsNewRole
  when?: 'several-businesses'
}

export interface WhatsNewRelease {
  /** Stable; what "seen" remembers. */
  id: string
  title: string
  /** The day it went live (YYYY-MM-DD); null while it is only on staging. */
  date: string | null
  entries: WhatsNewEntry[]
  fixed: WhatsNewFix[]
}

export const RELEASES: WhatsNewRelease[] = [
  {
    id: '2026-11-kartik',
    title: 'Kartik 2083 update',
    date: '2026-11-05',
    entries: [
      // Restaurants
      {
        id: 'kitchen-counter',
        title: 'Kitchen and Counter, full screen',
        line: 'Turn a tablet into the kitchen’s ticket wall or the counter’s till. A PIN keeps it there, so nobody wanders into Settings.',
        area: 'kitchen',
        icon: 'screen',
        for: ['restaurant'],
        minRole: 'staff',
      },
      {
        id: 'tickets-after-paid',
        title: 'Tickets stay up after the bill is paid',
        line: 'The cook sees “Paid” on the ticket for an hour instead of it vanishing. Checkout says when food is still cooking.',
        area: 'kitchen',
        icon: 'ticket',
        for: ['restaurant'],
        minRole: 'waiter',
        show: { label: 'Open Kitchen', to: '/kitchen' },
      },
      {
        id: 'reports-servers',
        title: 'Sales by server and by table',
        line: 'Reports now read like the dashboard: who sold what, and which tables earn the most.',
        area: 'insight',
        icon: 'chart',
        for: ['restaurant'],
        minRole: 'manager',
        show: { label: 'Open Reports', to: '/reports?tab=servers' },
      },
      {
        id: 'receive-ingredients',
        title: 'Ingredients come in like the paper bill',
        line: 'Free items in their own box, and a question when a rate looks wrong. A manager can correct a rate later.',
        area: 'buy',
        icon: 'truck',
        for: ['restaurant'],
        minRole: 'staff',
        show: { label: 'Receive stock', to: '/stock' },
      },
      // Shops and workshops
      {
        id: 'receive-bill',
        title: 'Receive stock reads like the supplier’s bill',
        line: 'Lines in the bill’s order, free items in their own box, and a question when a rate looks wrong. Typed a wrong rate? A manager can correct it later.',
        area: 'buy',
        icon: 'truck',
        for: ['shops', 'workshop'],
        minRole: 'staff',
        show: { label: 'Receive stock', to: '/stock' },
      },
      {
        id: 'two-units',
        title: 'Sell by the carton and by the piece',
        line: 'Choose both units from your own, give each its own price, and see what the bill will show before you save. Add product is grouped too, with every amount saying what it is per.',
        area: 'stock',
        icon: 'box',
        for: ['shops'],
        minRole: 'staff',
        show: { label: 'Open Products', to: '/products' },
      },
      {
        id: 'low-stock-email',
        title: 'Low-stock email: the ten that matter',
        line: 'The newest ten low products, then the rest as one number. No more endless lists.',
        area: 'stock',
        icon: 'mail',
        for: ['shops', 'workshop'],
        minRole: 'manager',
        show: { label: 'Email alerts', to: '/settings?tab=business' },
      },
      {
        id: 'expiry-month',
        title: 'Expiry typed as on the pack',
        line: 'Type 06/27 and see June 2027. The month is enough when the pack has no day.',
        area: 'stock',
        icon: 'calendar',
        for: ['shops'],
        minRole: 'staff',
        show: { label: 'Open Stock', to: '/stock' },
      },
      {
        id: 'business-kinds',
        title: 'Up to three kinds of business, and services too',
        line: 'A pharmacy that also sells phones gets the fields of both. Turn on “We also provide services” to sell repairs or fitting by the job. Agrovets are new, with their own fields and examples.',
        area: 'neutral',
        icon: 'check',
        for: ['shops', 'workshop'],
        minRole: 'admin',
        show: { label: 'Settings → Business', to: '/settings?tab=business' },
      },
      {
        id: 'services-own-form',
        title: 'Services have their own form',
        line: 'Add service makes a service, sold by the job and never counted. Add product shows Track stock again, and a product sold without counting reads “Not counted”, not “Service”.',
        area: 'stock',
        icon: 'box',
        for: ['shops', 'workshop'],
        minRole: 'staff',
        show: { label: 'Products → Not counted', to: '/products' },
      },
      {
        id: 'sizes-colours',
        title: 'Sizes and colours, as a grid',
        line: 'Tap a size run and the colours, then type the stock in one grid with a photo for each colour. The bill picks the colour by its photo, then the size, and Receive stock takes a whole grid at once.',
        area: 'stock',
        icon: 'box',
        for: ['apparel'],
        minRole: 'staff',
        show: { label: 'Open Products', to: '/products' },
      },
      {
        id: 'kitchen-tickets',
        title: 'KOT and BOT on tickets, and print or not as you send',
        line: 'Tickets read KOT 12 for the kitchen and BOT 13 for the bar. The arrow beside Send to kitchen sends with or without paper, and this device remembers it. ⋯ on a ticket prints it again, marked COPY.',
        area: 'kitchen',
        icon: 'ticket',
        for: ['restaurant'],
        minRole: 'waiter',
        show: { label: 'Open Kitchen', to: '/kitchen' },
      },
      // Every business
      {
        id: 'day-report',
        title: 'Your day by email, after closing',
        line: 'Sales, bills, money by method and anything to check. Restaurants also see guests, tables and servers.',
        area: 'insight',
        icon: 'mail',
        for: ['all'],
        minRole: 'manager',
        show: { label: 'Settings → Day report', to: '/settings?tab=day-report' },
      },
      {
        id: 'dashboard',
        title: 'A calmer dashboard',
        line: 'The same numbers, easier to read: this month against last, today in full colour.',
        area: 'sell',
        icon: 'home',
        for: ['all'],
        minRole: 'staff',
        show: { label: 'Open the dashboard', to: '/' },
      },
      {
        id: 'reports-look',
        title: 'Reports that read like the dashboard',
        line: 'Sales and Profit as one clear chart, your best sellers as plain rows, and money owed by age.',
        area: 'insight',
        icon: 'chart',
        for: ['shops', 'workshop'],
        minRole: 'manager',
        show: { label: 'Open Reports', to: '/reports' },
      },
      {
        id: 'email-switches',
        title: 'Choose your own emails',
        line: 'Each person can turn the low-stock email and the day report on or off for themselves.',
        area: 'neutral',
        icon: 'bell',
        for: ['all'],
        minRole: 'manager',
        show: { label: 'Email alerts', to: '/settings?tab=business' },
      },
    ],
    fixed: [
      {
        id: 'several-businesses',
        text: 'Switching between several businesses no longer stops with “Couldn’t open the local database”.',
        for: ['all'],
        minRole: 'viewer',
        when: 'several-businesses',
      },
      {
        id: 'thermal-print',
        text: 'Receipts and kitchen tickets print from the top of the paper and never sideways. On a thermal printer, choose the roll paper (80 × 3276 mm) and Margins “None”.',
        for: ['all'],
        minRole: 'staff',
      },
    ],
  },
]
